import React, { cloneElement, type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Typography } from '../Typography';

import { Container, TipContainer, TipRight, Title, ToolTipCanvas, ToolTipContainer } from './styles';

export type ToolTipProps = {
    children: ReactElement;
    classes?: string[];
    hoverRef?: React.RefObject<HTMLElement> | React.RefObject<HTMLElement>[] | null;
    text: string | ReactElement;
    title?: string;
};

export const ToolTip = ({ children, classes, hoverRef, text, title }: ToolTipProps) => {
    const ref = useRef<HTMLUListElement>(null);
    const toolTipRef = useRef<HTMLDivElement>(null);
    const [referenceTop, setReferenceTop] = useState<number>(0);
    // const [referenceHeight, setReferenceHeight] = useState<number>(0);
    const [referenceWidth, setReferenceWidth] = useState<number>(0);
    // const [toolTipHeight, setToolTipHeight] = useState<number>(0);
    const [toolTipVerticalMiddle, setToolTipVerticalMiddle] = useState<number>(0);
    const [isOpen, setIsOpen] = useState<boolean>(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            const refTop = ref.current ? ref.current.offsetTop : 0;
            const refHeight = ref.current ? ref.current.offsetHeight : 0;
            const refWidth = ref.current ? ref.current.offsetWidth : 0;
            const toolTipHeight = toolTipRef.current ? toolTipRef.current.offsetHeight : 0;

            let newToolTipMiddlePosition = refTop;
            if (refHeight > 0 && toolTipHeight > 0) {
                newToolTipMiddlePosition = refTop - Math.abs(refHeight - toolTipHeight) / 2;
            }

            setReferenceTop(refTop);
            // setReferenceHeight(refHeight);
            setReferenceWidth(refWidth);
            // setToolTipHeight(toolTipHeight);
            setToolTipVerticalMiddle(newToolTipMiddlePosition);
        }, 1000);
        return () => clearTimeout(timer);
    }, []);

    const toggleElementRef = useMemo(() => {
        if (hoverRef === null) {
            return null;
        } else if (Array.isArray(hoverRef)) {
            return hoverRef;
        } else {
            return [hoverRef];
        }
    }, [hoverRef]);

    const setToolTipOpen = useCallback(() => {
        setIsOpen(true);
    }, []);

    const setToolTipClose = useCallback(() => {
        setIsOpen(false);
    }, []);

    const setListeners = useCallback(
        (ref) => {
            const node = ref.current;
            if (node) {
                node.addEventListener('mouseover', setToolTipOpen);
                node.addEventListener('mouseout', setToolTipClose);
                return () => {
                    node.removeEventListener('mouseover', setToolTipOpen);
                    node.removeEventListener('mouseout', setToolTipClose);
                };
            }
        },
        [setToolTipOpen, setToolTipClose],
    );

    useEffect(() => {
        if (toggleElementRef && toggleElementRef[0]) {
            return setListeners(toggleElementRef[0]);
        }
    }, [toggleElementRef, setListeners]);

    useEffect(() => {
        if (toggleElementRef && toggleElementRef[1]) {
            return setListeners(toggleElementRef[1]);
        }
    }, [toggleElementRef, setListeners]);

    return (
        <Container className={classes ? classes.join(' ') : ''}>
            <div>
                <ToolTipContainer
                    isOpen={isOpen}
                    ref={toolTipRef}
                    style={{ right: referenceWidth, top: toolTipVerticalMiddle || referenceTop }}
                >
                    <ToolTipCanvas>
                        {!!title && (
                            <Title>
                                <Typography type="label" size={20}>
                                    {title}
                                </Typography>
                            </Title>
                        )}
                        <Typography type="paragraph" size={16}>
                            {text}
                        </Typography>
                    </ToolTipCanvas>
                    <TipContainer>
                        <TipRight />
                    </TipContainer>
                </ToolTipContainer>
            </div>
            {cloneElement(children, { ref })}
        </Container>
    );
};
